import React, {
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  DragDropContext,
} from '@hello-pangea/dnd';
import clsx from 'clsx';
import {
  Props as TabProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import {
  useGetPatientInsurances,
  useGetPatientSummary,
} from 'views/EMR/components/Insurance/hooks';
import {
  getPatientDemographics,
} from 'pages/Dashboard/utils/patient';
import {
  insurancesEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  PatientDTO,
} from 'dtos/patientDTO';
import InsuranceMediaList from 'pages/Dashboard/pages/Charts/pages/Details/components/InsuranceMediaList';
import {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import Skeleton from 'core/components/Skeleton';
import InsuranceCard from 'views/EMR/components/Insurance/InsuranceCard';
import useInsuranceCardDnD from 'views/EMR/components/Insurance/useInsuranceCardDnD';

type Props = Pick<TabProps, 'patient'>;

function InsuranceSkeleton() {
  return (
    <div className="w-[calc(50%-12px)] insurance-card flex flex-col gap-2 border border-blue-100 rounded-md shadow-sm min-w-[350px]">
      {/* Header Section */}
      <div className="flex justify-between bg-blue-50 px-4 py-2 rounded-t-md border-b border-blue-100 min-h-[80px]">
        <div className="flex flex-col gap-1 flex-1">
          <Skeleton className="!w-48 !h-4" />
          <Skeleton className="!w-36 !h-3" />
          <Skeleton className="!w-56 !h-3" />
        </div>
        <div className="flex flex-col justify-between items-end">
          <div className="flex gap-1">
            <Skeleton className="!w-6 !h-6 !rounded-full" />
            <Skeleton className="!w-6 !h-6 !rounded-full" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="!w-16 !h-[22px] !rounded-md" />
            <Skeleton className="!w-16 !h-[22px] !rounded-md" />
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="flex flex-col gap-3 px-4 pb-4 flex-1">
        {/* Effective dates section */}
        <div className="flex flex-col items-start text-xs">
          <Skeleton className="!w-16 !h-3 mb-1" />
          <div className="flex items-center justify-between w-full flex-wrap gap-2">
            <div className="flex gap-4">
              <Skeleton className="!w-24 !h-3" />
              <Skeleton className="!w-24 !h-3" />
            </div>
            <Skeleton className="!w-20 !h-6 !rounded-md" />
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Two column grid */}
        <div className="grid grid-cols-2 gap-16 text-sm flex-1">
          {/* Left Column */}
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <Skeleton className="!w-16 !h-3" />
              <Skeleton className="!w-20 !h-3" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="!w-12 !h-3" />
              <Skeleton className="!w-16 !h-3" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="!w-28 !h-3" />
              <Skeleton className="!w-24 !h-3" />
            </div>
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <Skeleton className="!w-20 !h-3" />
              <Skeleton className="!w-24 !h-3" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="!w-24 !h-3" />
              <Skeleton className="!w-20 !h-3" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="!w-22 !h-3" />
              <Skeleton className="!w-28 !h-3" />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Subscriber section */}
        <div>
          <Skeleton className="!w-16 !h-3 mb-1" />
          <Skeleton className="!w-64 !h-3 mb-1" />
          <Skeleton className="!w-48 !h-3 mb-1" />
          <Skeleton className="!w-32 !h-3" />
        </div>
      </div>
    </div>
  );
}

export default function InsuranceList({ patient }: Props) {
  const { isPanel } = useContext(ViewContext);
  const patientId = patient?.patientId ?? 0;
  const {
    activeDragSourceKey,
    busyContainerKeys,
    isDraggingInsuranceCard,
    onDragEnd,
    onDragStart,
    registerContainerItems,
    registerInsuranceRefetch,
    registerUnassociatedRefetch,
    unregisterContainerItems,
    unregisterInsuranceRefetch,
    unregisterUnassociatedRefetch,
  } = useInsuranceCardDnD({ patientId });
  const {
    data: insuranceList,
    isLoading,
    refetch,
  } = useGetPatientInsurances(patientId);

  const {
    data: patientSummary,
  } = useGetPatientSummary(patientId);

  useEffect(() => {
    const unsubscribe = insurancesEventChannel.on('onPatientInsuranceUpdated', async (_patientId) => {
      if (_patientId === patientId) {
        await refetch();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [patientId, refetch]);

  const patientDemographics = useMemo(
    () => getPatientDemographics(patientSummary ?? {}),
    [patientSummary],
  );

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className={clsx('flex w-full h-full min-h-0', {
        'flex-col gap-4': isPanel,
      })}
      >
        <div className={clsx('flex flex-col min-h-0', {
          'w-3/4 min-h-full': !isPanel,
          'flex-none w-full': isPanel,
        })}
        >
          <div className="flex flex-wrap gap-6 w-full pb-4">
            {isLoading
              ? (
                <>
                  <InsuranceSkeleton />
                  <InsuranceSkeleton />
                </>
              )
              : insuranceList?.map((insurance, index) => (
                <InsuranceCard
                  key={insurance.insuranceId}
                  index={index}
                  insurances={insuranceList}
                  patient={(patient ?? {}) as PatientDTO}
                  patientDemographics={patientDemographics ?? {}}
                  activeDragSourceKey={activeDragSourceKey}
                  busyContainerKeys={busyContainerKeys}
                  isDraggingInsuranceCard={isDraggingInsuranceCard}
                  shouldShowDragHint={index === 0}
                  registerContainerItems={registerContainerItems}
                  registerInsuranceRefetch={registerInsuranceRefetch}
                  unregisterContainerItems={unregisterContainerItems}
                  unregisterInsuranceRefetch={unregisterInsuranceRefetch}
                />
              ))}
            {!isLoading && insuranceList?.length === 0 && (
              <div className="flex text-sm">
                <span>No insurance policies found</span>
              </div>
            )}
          </div>
        </div>
        <InsuranceMediaList
          patient={patient}
          activeDragSourceKey={activeDragSourceKey}
          busyContainerKeys={busyContainerKeys}
          isDraggingInsuranceCard={isDraggingInsuranceCard}
          registerContainerItems={registerContainerItems}
          registerUnassociatedRefetch={registerUnassociatedRefetch}
          unregisterContainerItems={unregisterContainerItems}
          unregisterUnassociatedRefetch={unregisterUnassociatedRefetch}
        />
      </div>
    </DragDropContext>
  );
}
